import { AuthenticationService } from "../../core/authenticationService/AuthenticationService";
import { ImageUploadService } from "../../core/imageUploadService/ImageUploadService";
import { UserService } from "../../core/userService/UserService";
import { CoreError, CoreErrorCause } from "../models/CoreError";
import {
  hasMobileNotificationTokens,
  parseEmailNotificationsPreference,
  resolveEmailNotificationsEnabled,
} from "../models/EmailNotificationsPreference";
import { Gender } from "../models/Gender";
import { User } from "../models/User";

export class UserFeature {
  private static getAuthUserIdOrThrow(): string {
    const userId = AuthenticationService.getInstance().userId;
    if (!userId) {
      throw new CoreError({
        cause: CoreErrorCause.Forbidden,
        context: "No authenticated user available for user feature",
      });
    }
    return userId;
  }

  public static async loadCurrentUser(options?: {
    suppressMissingProfileReporting?: boolean;
  }): Promise<User> {
    const userId = this.getAuthUserIdOrThrow();
    return UserService.getInstance().loadUser(userId, options);
  }

  public static observeCurrentUser(
    onUserChanged: (user: User) => void,
    onError: (error: CoreError) => void,
  ): () => void {
    const userId = this.getAuthUserIdOrThrow();
    return UserService.getInstance().observeUser(
      userId,
      onUserChanged,
      onError,
    );
  }

  public static async saveName(fullName: string): Promise<void> {
    const userId = this.getAuthUserIdOrThrow();
    await UserService.getInstance().updateUserName(userId, fullName.trim());
  }

  public static async saveGender(gender: Gender): Promise<void> {
    const userId = this.getAuthUserIdOrThrow();
    await UserService.getInstance().updateUserGender(userId, gender);
  }

  public static async saveProfilePhoto(file: File): Promise<string> {
    const userId = this.getAuthUserIdOrThrow();
    const photoUrl = await ImageUploadService.getInstance().uploadProfilePhoto(
      file,
      userId,
    );
    await UserService.getInstance().updateUserProfilePhoto(userId, photoUrl);
    return photoUrl;
  }

  public static async deleteCurrentUser(): Promise<void> {
    const userId = this.getAuthUserIdOrThrow();
    await UserService.getInstance().deleteUser(userId);
  }

  /**
   * Emits the effective email-notifications state (the same resolution the
   * backend sender applies) now and on every change — including a
   * server-side unsubscribe-link write, so the toggle flips instantly.
   */
  public static observeEmailNotificationsEnabled(
    onChanged: (enabled: boolean) => void,
    onError: (error: CoreError) => void,
  ): () => void {
    const userId = this.getAuthUserIdOrThrow();
    const service = UserService.getInstance();
    return service.observeEmailNotificationsPreference(
      userId,
      (rawValue) => {
        const preference = parseEmailNotificationsPreference(rawValue);
        if (preference.enabled !== null) {
          // Explicit choice — no token reads needed.
          onChanged(preference.enabled);
          return;
        }
        service
          .loadNotificationTokenFacts(userId)
          .then(({ tokens, platforms }) => {
            onChanged(
              resolveEmailNotificationsEnabled(
                preference,
                hasMobileNotificationTokens(tokens, platforms),
              ),
            );
          })
          .catch(onError);
      },
      onError,
    );
  }

  /** Writes the explicit choice; the backend syncs Listmonk from it. */
  public static async setEmailNotificationsEnabled(
    enabled: boolean,
  ): Promise<void> {
    const userId = this.getAuthUserIdOrThrow();
    await UserService.getInstance().setEmailNotificationsEnabled(
      userId,
      enabled,
    );
  }
}
